// FieldNotesTab.js
// This is the Field Notes tab component for the Sampling Event Form

import React, { useState } from "react";
// Components
import { ConfirmationModal } from "../../common/ConfirmationModal";
import { FieldNoteModal } from "./components/FieldNoteModal";
import { NotesTabContent } from "./components/NotesTabContent";
import styles from "./FieldNotesTab.module.css";

// Tab title mapping
const TAB_TITLES = {
  myNotes: "My Notes",
  groupNotes: "Group Notes",
  allNotes: "All Notes",
};

// Main FieldNotesTab Component
export const FieldNotesTab = ({ samplingEventData, tabProps, actions }) => {
  const fnActions = actions?.samplingEventFormActions?.fieldNotes;
  const ui = tabProps?.fieldNotesUI || {};
  const user = tabProps?.user;

  // adding the emit patch from the tab props
  const emitPatch = tabProps?.emitPatch;

  // Current note being edited (for passing to modal in edit mode)
  const [currentNote, setCurrentNote] = useState(null);

  // Derive filtered notes per active tab + filters using actions
  const computeNotes = () => {
    if (!fnActions) return [];
    let notes = [];
    if (ui.activeTab === "myNotes" || !ui.activeTab) {
      notes = fnActions.getUserFieldNotes(user?.employee_id);
    } else if (ui.activeTab === "groupNotes" && ui.activeGroupId) {
      notes = fnActions.getGroupFieldNotes(ui.activeGroupId);
    } else if (ui.activeTab === "allNotes") {
      notes = fnActions.getAllFieldNotes();
    }
    return fnActions.applyAllFilters(notes, ui.filters || {});
  };
  const filteredNotes = computeNotes();

  // Get groups for groupNotes tab using actions
  const groups = fnActions?.getAllGroups() || [];
  // Get unique employees with notes for filter menu
  const employeesWithNotes = fnActions?.getUniqueEmployeesWithNotes() || [];

  // Handle opening modal for adding a new note
  const handleAddNote = () => {
    setCurrentNote(null);
    fnActions?.openModalForNew(user);
  };

  // Handle opening modal for editing an existing note
  const handleEditNote = (note) => {
    setCurrentNote(note);
    fnActions?.openModalForEdit(note);
  };

  // Handle saving from modal
  const handleSaveNote = async (tempState) => {
    if (!fnActions) return;

    const result = await fnActions.commitTempFieldNote(tempState, emitPatch);

    // Always close modal after save attempt
    fnActions.closeModal();
    setCurrentNote(null);

    return result;
  };

  // Handle modal close
  const handleCloseModal = () => {
    fnActions?.closeModal();
    setCurrentNote(null);
  };

  // Handle delete confirmation
  const handleDeleteNote = async () => {
    if (!fnActions || !ui.noteToDelete) return;

    await fnActions.removeFieldNote(ui.noteToDelete.id, emitPatch);
    fnActions.closeDeleteConfirm();
  };

  // Render correct content based on active tab
  const renderTabContent = () => {
    const activeTab = ui.activeTab || "myNotes";
    const title = TAB_TITLES[activeTab] || TAB_TITLES.myNotes;

    return (
      <NotesTabContent
        // Tab configuration
        title={title}
        activeTab={activeTab}
        // Notes data
        notes={filteredNotes}
        // User info for edit permissions
        user={user}
        // UI state
        isFilterMenuOpen={ui.isFilterMenuOpen}
        filters={ui.filters || {}}
        // Group-specific props
        groups={groups}
        activeGroupId={ui.activeGroupId}
        // Filter menu data
        employees={employeesWithNotes}
        // Actions
        onToggleFilter={() => fnActions?.toggleFilterMenu()}
        onApplyFilters={(f) => fnActions?.applyFilters(f)}
        onResetFilters={() => fnActions?.resetFilters()}
        onAddNote={handleAddNote}
        onEditNote={handleEditNote}
        onDeleteNote={(note) => fnActions?.openDeleteConfirm(note)}
        onSetActiveGroupId={(id) => fnActions?.setActiveGroupId(id)}
      />
    );
  };

  return (
    <div className={styles.fieldNotesContainer}>
      {/* Main Tab Navigation */}
      <div className={styles.tabNav}>
        <button
          className={`${styles.tabButton} ${
            ui.activeTab === "myNotes" ? styles.activeTab : ""
          }`}
          onClick={() => fnActions?.setActiveTab("myNotes")}
        >
          My Notes
        </button>
        <button
          className={`${styles.tabButton} ${
            ui.activeTab === "groupNotes" ? styles.activeTab : ""
          }`}
          onClick={() => fnActions?.setActiveTab("groupNotes", user)}
        >
          Group Notes
        </button>
        <button
          className={`${styles.tabButton} ${
            ui.activeTab === "allNotes" ? styles.activeTab : ""
          }`}
          onClick={() => fnActions?.setActiveTab("allNotes")}
        >
          All Notes
        </button>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Note Modal */}
      {ui.isNoteModalOpen && (
        <FieldNoteModal
          isAddMode={ui.isNoteAddMode}
          existingNote={currentNote}
          user={user}
          onClose={handleCloseModal}
          onSave={handleSaveNote}
          fnActions={fnActions}
        />
      )}

      {/* Confirmation Modal for Deletion */}
      {ui.isDeleteConfirmOpen && (
        <ConfirmationModal
          title="Delete Field Note"
          message="Are you sure you want to delete this field note? This action cannot be undone."
          onCancel={() => fnActions?.closeDeleteConfirm()}
          onConfirm={handleDeleteNote}
          confirmText="Delete"
          cancelText="Cancel"
          styles={styles}
          confirmButtonClass="confirmDelete"
        />
      )}
    </div>
  );
};
